export const DAILY_NEXT_OPEN_DISCLOSURE = 'Pinngro checks completed daily market data. A matching signal creates a suggestion in Today. Nothing is transacted unless you approve it before the deadline; an approved action may then attempt a virtual fill at the next eligible market session open. Stop-loss and take-profit thresholds request an exit suggestion and do not guarantee an exact price; overnight gaps can move past them.';

export const PRACTICE_EXPLANATION = 'Pinngro Practice uses virtual money, records virtual positions and fills, and never creates a real broker order. A Trading Strategy may suggest a BUY or SELL, but every new Practice bot requires your explicit approval in Today before a virtual transaction can be attempted.';

export const STRATEGY_PRESENTATION = {
  'trend-pullback': {
    name: 'Trend Retracement',
    summary: 'Looks for stocks that are already in a longer-term rise, waits for a pullback, and then waits for signs of recovery.',
    purpose: 'It tries to join a recovery within an existing uptrend. It is not trying to predict the exact bottom.',
    buy: 'It may buy after a stock in a rising longer-term trend pulls back, then closes back above its shorter trend with the required momentum confirmation.',
    sell: 'It may sell when the short recovery fails, the longer trend breaks, or a daily-close stop-loss or take-profit rule triggers.',
    protection: '6% stop-loss trigger and 18% take-profit trigger, both checked using completed daily data.',
    activity: 'Selective; positions may last weeks or months.',
    market: 'Markets where established upward trends continue after temporary pullbacks.',
    risk: 'Sideways markets can produce several false recoveries and smaller losing trades.',
    suitableFor: 'People who prefer to wait for a dip and recovery instead of buying a stock immediately after a rise.',
    style: 'Buys recovery in an existing rise',
    holding: 'Weeks to months',
    relativeRisk: 'Moderate',
    stopLoss: '6% trigger',
    takeProfit: '18% trigger',
    technicalRules: [
      'Entry requires the close above a rising 100-session average, a pullback-and-recross of the 20-session average, positive MACD(12,26), and RSI(14) from 40 through 70.',
      'Technical exit occurs on a close crossing below the 20-session average or a close below the 100-session average.',
      'Required production lookback: 120 completed daily bars.',
    ],
  },
  'momentum-breakout': {
    name: 'Momentum Breakout',
    summary: 'Looks for strong stocks making a significant new high with unusually strong trading activity.',
    purpose: 'It buys strength rather than weakness and waits for a confirmed breakout instead of trying to anticipate one.',
    buy: 'It may buy when a stock closes above every high from the previous 50 sessions, its shorter trend is above its longer trend, volume is unusually strong, and momentum is not beyond the strategy limit.',
    sell: 'It may sell when price breaks recent support, the trend regime fails, or a daily-close stop-loss or take-profit rule triggers.',
    protection: '5% stop-loss trigger and 15% take-profit trigger, both checked using completed daily data.',
    activity: 'Infrequent and selective; positions commonly last weeks or months.',
    market: 'Strong, directional markets where new highs attract continued participation.',
    risk: 'False breakouts can reverse sharply, and the price can gap before the next-session entry or exit.',
    suitableFor: 'People comfortable buying strength and accepting that some breakouts fail quickly.',
    style: 'Buys strength and new highs',
    holding: 'Weeks to months',
    relativeRisk: 'Higher',
    stopLoss: '5% trigger',
    takeProfit: '15% trigger',
    technicalRules: [
      'Entry requires the close above the highest high of the prior 50 sessions, SMA(20) above SMA(50), current volume at least 1.20 times prior-50-session average volume, and RSI(14) no higher than 85.',
      'Technical exit occurs below the lowest low of the prior 20 sessions or when SMA(20) falls below SMA(50).',
      'Required production lookback: 80 completed daily bars.',
    ],
  },
  'controlled-mean-reversion': {
    name: 'Controlled Mean Reversion',
    label: 'VERY SELECTIVE',
    summary: 'Very selective. Waits for an unusually deep pullback while the stock’s longer-term uptrend is still intact. Expected to trade infrequently and may remain inactive for months or years. A long period without a signal is expected and does not mean the strategy is broken.',
    purpose: 'It waits for unusually deep temporary weakness inside a longer-term uptrend, then requires a first sign of recovery.',
    buy: 'It may buy when a stock remains in a longer-term uptrend, closes at least 3% below its recent average, shows weak momentum, and then posts a higher daily close.',
    sell: 'It may sell when price returns to its recent average, momentum normalizes, or a daily-close stop-loss or take-profit rule triggers.',
    protection: '4% stop-loss trigger and 8% take-profit trigger, both checked using completed daily data.',
    activity: 'Very selective. It may remain inactive for months or years without being broken.',
    market: 'Uptrending stocks experiencing uncommon but controlled pullbacks.',
    risk: 'A temporary-looking dip may become a sustained decline.',
    suitableFor: 'Patient users who are comfortable waiting for uncommon pullback-and-recovery conditions.',
    style: 'Buys unusually deep temporary weakness',
    holding: 'Days to weeks',
    relativeRisk: 'Moderate, but highly selective',
    stopLoss: '4% trigger',
    takeProfit: '8% trigger',
    technicalRules: [
      'Entry requires close above SMA(100), close at least 3% below SMA(20), RSI(14) no higher than 35, close above the prior close, and ATR(14) divided by close no higher than 0.06.',
      'Technical exit occurs when close reaches SMA(20) or RSI(14) reaches 58.',
      'Required production lookback: 120 completed daily bars.',
    ],
  },
  'moving-average-crossover': {
    name: 'Moving Average Crossover',
    summary: 'A slower trend-following strategy that waits for the shorter-term trend to cross above the longer-term trend.',
    purpose: 'It deliberately reacts slowly so that a sustained trend has time to become visible.',
    buy: 'It may buy when the 20-session average crosses above the 50-session average and the stock closes above the longer average.',
    sell: 'It may sell when the averages cross back down, price stays below the longer average for three sessions, or the daily-close stop-loss rule triggers.',
    protection: '7% stop-loss trigger. There is no fixed take-profit; a position can remain open while the trend rules continue to hold.',
    activity: 'Very selective, with potentially long holding periods.',
    market: 'Sustained directional markets where slower trends persist.',
    risk: 'Sideways markets can create repeated losing crossovers, often called whipsaws.',
    suitableFor: 'People comfortable with slow signals and holding through longer trends without a fixed profit target.',
    style: 'Follows slower trend changes',
    holding: 'Potentially months',
    relativeRisk: 'Moderate',
    stopLoss: '7% trigger',
    takeProfit: 'No fixed take-profit',
    technicalRules: [
      'Entry requires SMA(20) to cross above SMA(50) using completed current and prior bars, with the close above SMA(50).',
      'Technical exit occurs on a downward SMA(20)/SMA(50) crossover or after three completed closes below SMA(50).',
      'Required production lookback: 60 completed daily bars.',
    ],
  },
  'the-big-short': {
    name: 'The Big Short',
    label: 'SHORT STRATEGY',
    summary: 'Looks for stocks in established downtrends that break below important support, aiming to profit in Practice if the price continues falling.',
    purpose: 'It waits for an established bearish trend, a fresh support breakdown, and stronger-than-normal volume instead of shorting a stock merely because it is falling.',
    buy: 'OPEN SHORT after the completed close breaks prior support while the bearish trend, volume, and RSI anti-chasing rules all agree.',
    sell: 'COVER SHORT when price crosses above its 20-session average, the 20-session average crosses above the 50-session average, or a daily risk/profit rule triggers.',
    protection: '5% short stop-loss trigger and 15% short take-profit trigger, checked using completed daily data. A later virtual cover can fill beyond either threshold.',
    activity: 'Selective; positions may remain open while the bearish rules continue to hold.',
    market: 'Established bearish trends with fresh, volume-confirmed support breakdowns.',
    risk: 'Short losses can grow rapidly if a stock rises. Unlike a normal long position, potential short losses are not capped by how far the stock can fall.',
    suitableFor: 'People who understand that a short position profits when price falls and loses when price rises.',
    style: 'Opens a short after bearish breakdown',
    holding: 'Days to months',
    relativeRisk: 'High · asymmetric short risk',
    stopLoss: '5% trigger above entry',
    takeProfit: '15% trigger below entry',
    technicalRules: [
      'Entry requires close below SMA(100), SMA(20) below SMA(50), SMA(50) below SMA(100), close below the prior 50-session low, volume at least 1.20 times prior mean volume, and RSI(14) at least 30.',
      'Technical cover occurs when close crosses above SMA(20) or SMA(20) crosses above SMA(50).',
      'Required production lookback: 120 completed daily bars. Prior 50-session windows exclude the current bar.',
    ],
  },
  'trend-pullback-v2-experimental': {
    name: 'Trend Confirmation',
    label: 'PRACTICE ONLY',
    summary: 'Waits for an additional completed daily close confirming recovery within an established uptrend before considering an entry. It uses the same exit and risk rules as Trend Retracement.',
    purpose: 'It waits for a second completed close confirming recovery before considering an entry in an existing uptrend.',
    buy: 'It may buy after the latest two completed closes are above their 20-session averages, following a close two sessions earlier at or below that average, with the same trend, MACD, and RSI rules as Trend Retracement.',
    sell: 'Uses the same exit, stop-loss and take-profit rules as Trend Retracement; only the entry confirmation differs.',
    protection: '6% stop-loss trigger and 18% take-profit trigger, both checked using completed daily data.',
    activity: 'Selective; uses an additional completed session of confirmation before entering.',
    market: 'Markets where established upward trends continue after temporary pullbacks.',
    risk: 'Waiting for confirmation can skip some recoveries while still leaving the strategy exposed to false recoveries and next-session gaps.',
    suitableFor: 'People who prefer an additional completed-session entry confirmation and understand it can miss some recoveries. Practice only.',
    style: 'Buys recovery in an existing rise, but waits for additional confirmation.',
    holding: 'Weeks to months',
    relativeRisk: 'Moderate',
    stopLoss: '6% trigger',
    takeProfit: '18% trigger',
    technicalRules: [
      'Entry uses the same rising SMA(100), positive MACD(12,26), and RSI(14) from 40 through 70 conditions as Trend Retracement.',
      'The latest two completed closes must be above SMA(20), following a close two sessions earlier at or below SMA(20).',
      'Technical exits, 6% stop-loss and 18% take-profit rules are unchanged; required production lookback: 120 completed daily bars.',
    ],
  },
};

// Presentation is shared by selection, comparison, Help, and bot review. The
// server remains the execution authority for factory and experimental rules.
export const FACTORY_STRATEGY_EDUCATION = STRATEGY_PRESENTATION;

// Customer copy may evolve while immutable strategy family and saved names stay intact.
export const strategyDisplayName = (slug, fallback) => {
  if (!fallback || fallback === 'Strategy identity unavailable' || fallback === 'Saved strategy unavailable') {
    return fallback || 'Strategy identity unavailable';
  }
  if (['Trend Pullback', 'Factory Trend Pullback'].includes(fallback)) return 'Trend Retracement';
  if (['Trend Pullback v2', 'Trend Pullback v2 – Experimental'].includes(fallback)) return 'Trend Confirmation';
  return STRATEGY_PRESENTATION[slug]?.name || fallback;
};

const pairingReason = {
  'Blue-Chip Stability': {
    'trend-pullback': 'Established stocks can form steadier trends, though pullback recoveries can still fail.',
    'momentum-breakout': 'Blue chips can break out, but they may do so less often than faster-growth stocks.',
    'controlled-mean-reversion': 'Steadier companies are a natural place to wait for an uncommon pullback inside an intact trend.',
    'moving-average-crossover': 'Slower trend signals fit the steadier, longer-horizon character of this stock focus.',
    'the-big-short': 'Established companies can provide more orderly bearish evidence, though every short can still rise sharply.',
  },
  'Growth Momentum': {
    'trend-pullback': 'Growth stocks often trend strongly, and this strategy waits for a recovery after a dip.',
    'momentum-breakout': 'Both the stock focus and strategy look for strength and continuing momentum.',
    'controlled-mean-reversion': 'Fast-moving growth stocks can keep falling after an apparent dip, increasing risk.',
    'moving-average-crossover': 'A slower trend signal can participate in growth trends, but it may enter late.',
    'the-big-short': 'Growth stocks can reverse or squeeze upward quickly, increasing short risk.',
  },
  'Value Investing': {
    'trend-pullback': 'A value candidate can also trend, but cheap valuation alone does not create a recovery signal.',
    'momentum-breakout': 'Value candidates are not selected for new-high momentum, so signals may be uncommon or mismatched.',
    'controlled-mean-reversion': 'The combination patiently seeks temporary weakness in an otherwise intact longer trend.',
    'moving-average-crossover': 'A slower trend confirmation can complement valuation-focused stock selection.',
    'the-big-short': 'Low valuation can make continued downside less predictable even when bearish price rules match.',
  },
  'Profitable Growth': {
    'trend-pullback': 'Smaller growth companies can recover within trends, but their price moves may be less stable.',
    'momentum-breakout': 'Breakouts can occur, but smaller-company gaps and false moves raise the risk.',
    'controlled-mean-reversion': 'A deep pullback in a smaller growth company may continue rather than recover.',
    'moving-average-crossover': 'Slow signals may reduce noise, but smaller companies can still whipsaw sharply.',
    'the-big-short': 'Growth-oriented companies can rebound sharply even after a bearish breakdown.',
  },
  'High Volatility': {
    'trend-pullback': 'Large swings can create apparent recoveries that fail quickly.',
    'momentum-breakout': 'Strong moves can continue, but false breakouts and next-open gaps can be severe.',
    'controlled-mean-reversion': 'Sharp declines in highly volatile stocks can continue rather than recover.',
    'moving-average-crossover': 'Frequent reversals can create repeated crossover losses.',
    'the-big-short': 'Extreme volatility and gaps can make a later virtual cover materially worse than the stop threshold.',
  },
  'Dividend Quality': {
    'trend-pullback': 'Steadier dividend-paying candidates may offer clearer pullback-and-recovery patterns.',
    'momentum-breakout': 'Income-oriented candidates are not usually selected for strong new-high breakouts.',
    'controlled-mean-reversion': 'Patient pullback entries fit the typically steadier character of this stock focus.',
    'moving-average-crossover': 'A slower trend strategy can suit longer-horizon dividend-oriented candidates.',
    'the-big-short': 'Dividend-oriented candidates are not selected for bearish behavior and can be poor short candidates.',
  },
  'Technology Leaders': {
    'trend-pullback': 'Large growth candidates often trend, while the strategy waits for a recovery instead of chasing.',
    'momentum-breakout': 'Growth-oriented candidates and strength-based breakout rules naturally align.',
    'controlled-mean-reversion': 'Fast-moving growth names can turn a deep pullback into a sustained decline.',
    'moving-average-crossover': 'Slow trend confirmation can participate, but may react late to rapid technology moves.',
    'the-big-short': 'Technology leaders can form bearish breaks but may also reverse sharply on news or expectations.',
  },
  'Defensive Stocks': {
    'trend-pullback': 'Steadier candidates can form recoveries, though their trends may be less pronounced.',
    'momentum-breakout': 'Defensive candidates are not usually chosen for strong breakout behavior.',
    'controlled-mean-reversion': 'Lower-volatility candidates can suit patient, uncommon pullback entries.',
    'moving-average-crossover': 'Slow trend signals fit longer-horizon defensive stock behavior.',
    'the-big-short': 'Defensive candidates may provide more orderly signals, though bearish breaks can still fail.',
  },
};

export const pairingExplanation = (stockFocus, strategySlug, label) => (
  pairingReason[stockFocus]?.[strategySlug]
  || `${label || 'This pairing'} is guidance based on how the stock focus and trading style tend to interact; it never blocks your choice.`
);

export const FACTORY_STRATEGY_LIST = Object.entries(FACTORY_STRATEGY_EDUCATION)
  .filter(([slug]) => !['the-big-short', 'trend-pullback-v2-experimental'].includes(slug))
  .map(([slug, strategy]) => ({ slug, ...strategy }));

export const BIG_SHORT_EDUCATION = {
  slug: 'the-big-short',
  ...FACTORY_STRATEGY_EDUCATION['the-big-short'],
};

export const PINNGRO_STRATEGY_LIST = Object.entries(STRATEGY_PRESENTATION)
  .map(([slug, strategy]) => ({ slug, ...strategy }));
